import Link from "next/link";
import Image from "next/image";
import { listPosts } from "@/lib/cms";
import { PreviewBar } from "../_bar";

export const metadata = { title: "Mockup A · Quiet Editorial · Preview" };
export const dynamic = "force-dynamic";

const CAT_IDS = { tinNong: 148, phanTich: 36, crypto: 37, vang: 252, chungKhoan: 40 };

/**
 * MOCKUP A — Quiet Editorial.
 * Aeon / The Atlantic / The Drift inspired. No hero photo. Massive serif
 * headline as a typographic statement. Drop-cap. Italic standfirst.
 * Generous whitespace. Single accent color (deep ink + paper).
 */
export default async function MockupAPage() {
  const [
    { posts: feat },
    { posts: latest },
    { posts: phanTich },
    { posts: tinNong },
    { posts: crypto },
  ] = await Promise.all([
    listPosts({ perPage: 1, featured: true, orderBy: "date", order: "desc" }),
    listPosts({ perPage: 8, orderBy: "date", order: "desc" }),
    listPosts({ perPage: 4, categories: [CAT_IDS.phanTich] }),
    listPosts({ perPage: 5, categories: [CAT_IDS.tinNong] }),
    listPosts({ perPage: 3, categories: [CAT_IDS.crypto] }),
  ]);

  const heroPost = feat[0] ?? latest[0];
  const used = new Set<string | number>();
  if (heroPost) used.add(heroPost.id);
  const trio = latest.filter((p) => !used.has(p.id)).slice(0, 3);
  trio.forEach((p) => used.add(p.id));
  const longread = phanTich[0];
  if (longread) used.add(longread.id);
  const longreadLinks = phanTich.slice(1, 4);

  return (
    <div style={{ background: "#FBF8F0", color: "#1A1410", minHeight: "100vh" }}>
      <PreviewBar active="a" />

      {/* Masthead */}
      <header
        className="border-b"
        style={{ borderColor: "#D8CDB5" }}
      >
        <div className="mx-auto max-w-[1240px] px-8 lg:px-14 pt-10 pb-7">
          <div className="flex items-baseline justify-between gap-6 mb-7">
            <span className="text-[11px] uppercase tracking-[0.3em] font-mono text-[#7A6B55]">
              Số {new Date().toLocaleDateString("vi-VN", { day: "2-digit", month: "long" })} · Tài chính · Phân tích
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-mono text-[#7A6B55] hidden sm:inline">
              Vietnam · vi-VN
            </span>
          </div>
          <Link
            href="/"
            className="block text-center"
            style={{
              fontFamily: "var(--font-fraunces)",
              fontSize: "clamp(64px, 11vw, 156px)",
              lineHeight: 0.9,
              letterSpacing: "-0.04em",
              fontWeight: 400,
              fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1",
            }}
          >
            Finzone
          </Link>
          <p className="text-center mt-4 italic text-[15px] text-[#5C4F3A]"
            style={{ fontFamily: "var(--font-newsreader)" }}>
            Tin tài chính · Phân tích thị trường · Cho người đầu tư có suy nghĩ
          </p>
          <nav className="mt-9 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-[13px] tracking-wide uppercase">
            {["/category/tin-nong","/category/phan-tich","/category/crypto","/category/chung-khoan","/category/vang","/category/forex"].map((href, i) => {
              const label = ["Tin nóng","Phân tích","Crypto","Chứng khoán","Vàng","Forex"][i];
              return (
                <Link key={href} href={href} className="hover:underline underline-offset-[6px] decoration-[#9C6B2A]">
                  {label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <main>
        {/* HERO — pure typography, no photo */}
        {heroPost && (
          <section className="border-b" style={{ borderColor: "#D8CDB5" }}>
            <div className="mx-auto max-w-[1240px] px-8 lg:px-14 py-16 lg:py-28">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-9">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#9C6B2A] mb-7">
                    {heroPost.categories[0]?.name ?? "Trang nhất"} · Đặc biệt
                  </div>
                  <Link href={`/${heroPost.slug}`} className="group block">
                    <h1
                      className="group-hover:text-[#9C6B2A] transition-colors"
                      style={{
                        fontFamily: "var(--font-fraunces)",
                        fontSize: "clamp(40px, 6.4vw, 96px)",
                        lineHeight: 1.02,
                        letterSpacing: "-0.025em",
                        fontWeight: 400,
                        fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1",
                      }}
                    >
                      {heroPost.title}
                    </h1>
                  </Link>
                  {heroPost.excerpt && (
                    <p
                      className="mt-7 max-w-3xl italic text-[#3A2F22] text-[20px] lg:text-[24px] leading-[1.55]"
                      style={{ fontFamily: "var(--font-newsreader)", fontWeight: 400 }}
                    >
                      {heroPost.excerpt}
                    </p>
                  )}
                  <div className="mt-9 flex items-center gap-3 text-[12px] uppercase tracking-[0.2em] text-[#7A6B55] font-mono">
                    <span className="font-semibold text-[#3A2F22]">{heroPost.author.name}</span>
                    <span>·</span>
                    <span>{heroPost.readMinutes} phút đọc</span>
                    <span>·</span>
                    <span>{new Date(heroPost.date).toLocaleDateString("vi-VN")}</span>
                  </div>
                </div>
                <aside className="lg:col-span-3 lg:border-l lg:pl-8" style={{ borderColor: "#D8CDB5" }}>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#9C6B2A] mb-5">
                    Cũng đáng đọc
                  </div>
                  <ul className="space-y-5">
                    {trio.map((p) => (
                      <li key={p.id} className="border-b pb-5 last:border-b-0" style={{ borderColor: "#E5DCC7" }}>
                        <Link href={`/${p.slug}`} className="block group">
                          <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#9C6B2A] mb-2">
                            {p.categories[0]?.name ?? "Tin"}
                          </div>
                          <h3
                            className="group-hover:text-[#9C6B2A] transition-colors"
                            style={{
                              fontFamily: "var(--font-fraunces)",
                              fontSize: 19,
                              lineHeight: 1.25,
                              fontWeight: 500,
                            }}
                          >
                            {p.title}
                          </h3>
                          <div className="mt-2 text-[11px] font-mono text-[#7A6B55]">
                            {p.readMinutes} phút · {p.author.name}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            </div>
          </section>
        )}

        {/* LONGREAD with drop-cap teaser */}
        {longread && (
          <section className="border-b" style={{ borderColor: "#D8CDB5", background: "#F4EDDC" }}>
            <div className="mx-auto max-w-[920px] px-8 lg:px-14 py-20 lg:py-28">
              <div className="text-center text-[10px] uppercase tracking-[0.35em] font-mono text-[#9C6B2A] mb-6">
                Long-read của tuần
              </div>
              <Link href={`/${longread.slug}`} className="group block text-center">
                <h2
                  className="group-hover:text-[#9C6B2A] transition-colors mb-7"
                  style={{
                    fontFamily: "var(--font-fraunces)",
                    fontSize: "clamp(32px, 5vw, 60px)",
                    lineHeight: 1.05,
                    letterSpacing: "-0.02em",
                    fontWeight: 400,
                    fontStyle: "italic",
                    fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                  }}
                >
                  {longread.title}
                </h2>
              </Link>
              {longread.excerpt && (
                <p
                  className="text-[18px] leading-[1.7] text-[#2A2218]"
                  style={{ fontFamily: "var(--font-newsreader)" }}
                >
                  <span
                    className="float-left mr-3 mt-1 leading-[0.85] text-[#9C6B2A]"
                    style={{
                      fontFamily: "var(--font-fraunces)",
                      fontSize: 80,
                      fontWeight: 500,
                      fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                    }}
                  >
                    {longread.excerpt.charAt(0).toUpperCase()}
                  </span>
                  {longread.excerpt.slice(1)}
                </p>
              )}
              <div className="mt-8 flex items-center gap-3 text-[12px] uppercase tracking-[0.2em] text-[#7A6B55] font-mono">
                <span className="font-semibold text-[#3A2F22]">{longread.author.name}</span>
                <span>·</span>
                <span>{longread.readMinutes} phút đọc</span>
                <span className="ml-auto group">
                  <Link href={`/${longread.slug}`} className="border-b border-[#3A2F22] pb-1 hover:text-[#9C6B2A] hover:border-[#9C6B2A]">
                    Đọc bài đầy đủ →
                  </Link>
                </span>
              </div>
              {longreadLinks.length > 0 && (
                <div className="mt-14 pt-8 border-t" style={{ borderColor: "#D8CDB5" }}>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#7A6B55] mb-4">
                    Cũng trong loạt phân tích
                  </div>
                  <ul className="space-y-3">
                    {longreadLinks.map((p) => (
                      <li key={p.id}>
                        <Link href={`/${p.slug}`} className="block group flex items-baseline gap-3">
                          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#9C6B2A] shrink-0 w-20">
                            {p.categories[0]?.name ?? "Bài"}
                          </span>
                          <span
                            className="flex-1 group-hover:text-[#9C6B2A] transition-colors"
                            style={{ fontFamily: "var(--font-fraunces)", fontSize: 18, fontWeight: 500 }}
                          >
                            {p.title}
                          </span>
                          <span className="text-[10px] font-mono text-[#7A6B55] shrink-0">{p.readMinutes}'</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* TIN NÓNG — 3 column text-only with rules */}
        {tinNong.length >= 3 && (
          <section className="border-b" style={{ borderColor: "#D8CDB5" }}>
            <div className="mx-auto max-w-[1240px] px-8 lg:px-14 py-16 lg:py-24">
              <div className="flex items-baseline justify-between mb-12 pb-3 border-b-2" style={{ borderColor: "#1A1410" }}>
                <h2
                  style={{
                    fontFamily: "var(--font-fraunces)",
                    fontSize: "clamp(28px, 3.6vw, 44px)",
                    fontWeight: 500,
                    fontStyle: "italic",
                    fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                  }}
                >
                  Tin nóng hôm nay
                </h2>
                <Link href="/category/tin-nong" className="text-[12px] uppercase tracking-[0.2em] font-mono text-[#9C6B2A] hover:underline">
                  Tất cả →
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10">
                {tinNong.slice(0, 3).map((p, i) => (
                  <article key={p.id} className={i > 0 ? "md:border-l md:pl-10" : ""} style={{ borderColor: "#E5DCC7" }}>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#9C6B2A] mb-3">
                      {p.categories[0]?.name ?? "Tin"}
                    </div>
                    <Link href={`/${p.slug}`} className="block group">
                      <h3
                        className="group-hover:text-[#9C6B2A] transition-colors mb-3"
                        style={{
                          fontFamily: "var(--font-fraunces)",
                          fontSize: 26,
                          lineHeight: 1.18,
                          fontWeight: 500,
                          fontVariationSettings: "'opsz' 72",
                        }}
                      >
                        {p.title}
                      </h3>
                    </Link>
                    {p.excerpt && (
                      <p
                        className="text-[15px] leading-[1.65] text-[#3A2F22] line-clamp-3 mb-3"
                        style={{ fontFamily: "var(--font-newsreader)" }}
                      >
                        {p.excerpt}
                      </p>
                    )}
                    <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-[#7A6B55]">
                      {p.author.name} · {p.readMinutes} phút
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CRYPTO — single editorial photo + caption strip */}
        {crypto.length > 0 && (
          <section className="border-b" style={{ borderColor: "#D8CDB5" }}>
            <div className="mx-auto max-w-[1240px] px-8 lg:px-14 py-16 lg:py-24">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                  {crypto[0]?.featured && (
                    <Link href={`/${crypto[0].slug}`} className="block group">
                      <div className="aspect-[5/4] relative overflow-hidden bg-[#E5DCC7]">
                        <Image
                          src={crypto[0].featured.url}
                          alt={crypto[0].featured.alt}
                          fill
                          sizes="(max-width: 1024px) 100vw, 60vw"
                          className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                      </div>
                      <p className="mt-4 text-[12px] italic text-[#7A6B55]" style={{ fontFamily: "var(--font-newsreader)" }}>
                        {crypto[0].featured.alt || crypto[0].title}
                      </p>
                    </Link>
                  )}
                </div>
                <div className="lg:col-span-5">
                  <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#9C6B2A] mb-4">
                    Crypto · Tuần qua
                  </div>
                  {crypto[0] && (
                    <Link href={`/${crypto[0].slug}`} className="block group">
                      <h2
                        className="group-hover:text-[#9C6B2A] transition-colors mb-5"
                        style={{
                          fontFamily: "var(--font-fraunces)",
                          fontSize: "clamp(28px, 3.4vw, 44px)",
                          lineHeight: 1.1,
                          fontWeight: 400,
                          fontVariationSettings: "'opsz' 144, 'SOFT' 30",
                        }}
                      >
                        {crypto[0].title}
                      </h2>
                    </Link>
                  )}
                  {crypto[0]?.excerpt && (
                    <p className="text-[16px] leading-[1.7] text-[#2A2218] mb-7" style={{ fontFamily: "var(--font-newsreader)" }}>
                      {crypto[0].excerpt}
                    </p>
                  )}
                  <ul className="space-y-4 border-t pt-5" style={{ borderColor: "#E5DCC7" }}>
                    {crypto.slice(1).map((p) => (
                      <li key={p.id}>
                        <Link href={`/${p.slug}`} className="group block flex items-baseline gap-4">
                          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#9C6B2A] shrink-0 w-12">→</span>
                          <span style={{ fontFamily: "var(--font-fraunces)", fontSize: 17, fontWeight: 500 }}
                            className="flex-1 group-hover:text-[#9C6B2A]">
                            {p.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer style={{ background: "#1A1410", color: "#FBF8F0" }}>
          <div className="mx-auto max-w-[1240px] px-8 lg:px-14 py-16 lg:py-20 text-center">
            <Link href="/" style={{
              fontFamily: "var(--font-fraunces)",
              fontSize: "clamp(56px, 8vw, 120px)",
              lineHeight: 0.9,
              letterSpacing: "-0.04em",
              fontWeight: 400,
              color: "#FBF8F0",
              fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1",
            }} className="inline-block">
              Finzone
            </Link>
            <p className="mt-5 italic text-[15px] text-[#C9BFA8]"
              style={{ fontFamily: "var(--font-newsreader)" }}>
              Tin tài chính · Phân tích thị trường · cho người đầu tư có suy nghĩ
            </p>
            <div className="mt-12 text-[11px] uppercase tracking-[0.25em] font-mono text-[#9C8B6F]">
              © {new Date().getFullYear()} Finzone Network · Vietnam
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
